import { useState } from "react";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, Copy } from "lucide-react";

interface JiraManualDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnected: () => void;
}

export function JiraManualDialog({
  open,
  onOpenChange,
  onConnected,
}: JiraManualDialogProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [siteUrl, setSiteUrl] = useState("");
  const [email, setEmail] = useState("");
  const [apiToken, setApiToken] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [connectResult, setConnectResult] = useState<{
    webhookUrl: string;
    webhookSecret: string;
  } | null>(null);

  const connectManualMutation = trpc.integration.jira.connectManual.useMutation(
    {
      onSuccess: (data) => {
        toast.success("Jira manual connection created");
        setConnectResult({
          webhookUrl: data.webhookUrl,
          webhookSecret: data.webhookSecret,
        });
        onConnected();
      },
      onError: (err) => {
        toastTrpcError(err, "Failed to connect Jira");
      },
    },
  );

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!siteUrl || !email || !apiToken) {
      toast.error("Please fill in site URL, email, and API token");
      return;
    }
    connectManualMutation.mutate({
      siteUrl,
      email,
      apiToken,
      displayName: displayName || undefined,
      webhookSecret: webhookSecret || undefined,
    });
  };

  const handleClose = () => {
    onOpenChange(false);
    setConnectResult(null);
    setSiteUrl("");
    setEmail("");
    setApiToken("");
    setDisplayName("");
    setWebhookSecret("");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
        else onOpenChange(true);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Connect Jira Manually (API Token)</DialogTitle>
          <DialogDescription>
            Use an Atlassian API token and manual webhook when OAuth 3LO is not
            permitted by your organisation.
          </DialogDescription>
        </DialogHeader>

        {connectResult ? (
          <div className="space-y-4">
            <div className="rounded-lg bg-emerald-500/10 p-3 text-sm text-emerald-600">
              Jira connection saved successfully! Follow these steps to register
              the webhook in Jira Admin:
            </div>

            <div className="space-y-3 text-xs">
              <ol className="list-decimal pl-4 space-y-2 text-muted-foreground">
                <li>
                  Log in as a Jira Administrator and navigate to{" "}
                  <strong className="text-foreground">
                    Jira Settings &gt; System &gt; WebHooks
                  </strong>
                  .
                </li>
                <li>
                  Click{" "}
                  <strong className="text-foreground">Create a WebHook</strong>.
                </li>
                <li>
                  Set the URL to:
                  <div className="mt-1 flex items-center gap-1 rounded bg-muted p-2 font-mono text-xs text-foreground">
                    <span className="truncate flex-1">
                      {connectResult.webhookUrl}
                    </span>
                    <SimpleTooltip
                      content={
                        copiedField === "url" ? "Copied!" : "Copy webhook URL"
                      }
                    >
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 shrink-0"
                        aria-label="Copy webhook URL"
                        onClick={() =>
                          handleCopy(connectResult.webhookUrl, "url")
                        }
                      >
                        {copiedField === "url" ? (
                          <Check className="h-3 w-3 text-emerald-500" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </SimpleTooltip>
                  </div>
                </li>
                <li>
                  Set the Secret to:
                  <div className="mt-1 flex items-center gap-1 rounded bg-muted p-2 font-mono text-xs text-foreground">
                    <span className="truncate flex-1">
                      {connectResult.webhookSecret}
                    </span>
                    <SimpleTooltip
                      content={
                        copiedField === "secret"
                          ? "Copied!"
                          : "Copy webhook secret"
                      }
                    >
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 shrink-0"
                        aria-label="Copy webhook secret"
                        onClick={() =>
                          handleCopy(connectResult.webhookSecret, "secret")
                        }
                      >
                        {copiedField === "secret" ? (
                          <Check className="h-3 w-3 text-emerald-500" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </SimpleTooltip>
                  </div>
                </li>
                <li>
                  Under{" "}
                  <strong className="text-foreground">
                    Issue related events
                  </strong>
                  , check <strong className="text-foreground">created</strong>,{" "}
                  <strong className="text-foreground">updated</strong>, and{" "}
                  <strong className="text-foreground">deleted</strong>.
                </li>
                <li>
                  Click <strong className="text-foreground">Create</strong> at
                  the bottom of the page.
                </li>
              </ol>
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleManualSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="jira-site-url">Jira Site URL</Label>
              <Input
                id="jira-site-url"
                placeholder="https://company.atlassian.net"
                value={siteUrl}
                onChange={(e) => setSiteUrl(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="jira-email">Atlassian Admin Email</Label>
              <Input
                id="jira-email"
                type="email"
                placeholder="admin@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="jira-token">Atlassian API Token</Label>
              <Input
                id="jira-token"
                type="password"
                placeholder="API token from id.atlassian.com/manage-profile/security/api-tokens"
                value={apiToken}
                onChange={(e) => setApiToken(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="jira-display-name">Display Name (optional)</Label>
              <Input
                id="jira-display-name"
                placeholder="Acme Jira"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="jira-webhook-secret">
                Webhook Secret (optional)
              </Label>
              <Input
                id="jira-webhook-secret"
                placeholder="Leave blank to auto-generate a secure secret"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Used for HMAC signature verification of incoming webhooks.
              </p>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={connectManualMutation.isPending}>
                {connectManualMutation.isPending ? "Connecting..." : "Connect"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
